import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { UsersThree } from "@phosphor-icons/react";
import { EmptyState, PageHeader, Select, Table, TBody, TD, TDNum, TFoot, TH, THead, TR, formatCount } from "@ghezaa/ui";
import { formatDateTime } from "@ghezaa/shell";
import type { TeamRow } from "@/api/client";
import { useTeam } from "@/api/queries";
import { change, inCurrency, money, monthName, monthOf, monthShort, percent } from "@/lib/format";
import { useCurrentMarket } from "@/lib/market";

export type TeamSearch = { month?: string };

export function teamSearch(search: Record<string, unknown>): TeamSearch {
  return typeof search.month === "string" && /^\d{4}-\d{2}$/.test(search.month) ? { month: search.month } : {};
}

/** The twelve months up to this one, newest first: `2026-11`, `2026-10`, … */
function lastMonths(): string[] {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
}

/**
 * The team's month: one row per salesperson code, what it sold (by the salesperson on BC's documents, as BC's Sales by
 * Salesperson counts it) and the state of its book. Each row opens that salesperson's customers.
 */
export function TeamPage() {
  const market = useCurrentMarket();
  const search = useSearch({ strict: false }) as TeamSearch;
  const navigate = useNavigate();
  const team = useTeam(market.code, search.month);
  const d = team.data;
  const months = lastMonths();
  const total = d?.total.now.revenue ?? 0;
  const prev = d ? monthShort(monthOf(d.before.from)) : "";
  const targeted = Boolean(d?.rows.some((r) => r.attainment !== null));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Team"
        description={d ? `${monthName(d.month)}${d.soFar ? " so far, against the same days of" : ", against"} ${monthName(monthOf(d.before.from))}.` : undefined}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Select aria-label="Month" value={search.month ?? d?.month ?? ""} onChange={(e) => void navigate({ to: ".", search: { month: e.target.value } as never, replace: true })} className="w-auto">
          {months.map((m) => (
            <option key={m} value={m}>
              {monthName(m)}
            </option>
          ))}
        </Select>
      </div>
      {team.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load the team: {team.error.message}</p>}
      {!d && !team.isError && <div className="h-40 bg-surface-2" aria-busy="true" />}
      {d && d.rows.length === 0 && <EmptyState icon={UsersThree} title="No sales in this month" description="Business Central has no invoice for the market in it. Try another month." />}
      {d && d.rows.length > 0 && (
        <>
          <Table>
            <THead>
              <TR>
                <TH>Salesperson</TH>
                {targeted && <TH from="md" className="text-end whitespace-nowrap">Of target</TH>}
                <TH bar className="text-end whitespace-nowrap">{inCurrency("Revenue", market.currency)}</TH>
                <TH from="sm" className="text-end whitespace-nowrap">vs {prev}</TH>
                <TH from="md" className="text-end">Margin</TH>
                <TH from="xl" className="text-end">Invoices</TH>
                <TH from="xl" className="text-end whitespace-nowrap">Lines an invoice</TH>
                <TH from="md" className="text-end">Customers</TH>
                <TH from="xl" className="text-end">New</TH>
                <TH from="xl" className="text-end">Silent</TH>
                <TH from="md" className="text-end whitespace-nowrap">{inCurrency("Overdue", market.currency)}</TH>
              </TR>
            </THead>
            <TBody>
              {d.rows.map((r) => (
                <Figures key={r.code || "none"} row={r} share={total > 0 ? Math.max(0, r.now.revenue) / total : 0} label={null} targeted={targeted} />
              ))}
            </TBody>
            <TFoot>
              <Figures row={d.total} share={null} label="Total" targeted={targeted} />
            </TFoot>
          </Table>
          <p className="text-xs text-ink-3">
            {d.readAt ? `Sales read from Business Central ${formatDateTime(d.readAt)}.` : "Business Central's sales have not been read yet."} Revenue and margin are before
            VAT, credit memos taken off; customers are those who bought, silent and overdue those in the code's book now.
          </p>
        </>
      )}
    </div>
  );
}

function Figures({ row, share, label, targeted }: { row: TeamRow; share: number | null; label: string | null; targeted: boolean }) {
  const name = label ?? (row.code === "" ? "No salesperson" : row.name || row.code);
  const sub = label ? null : row.code === "" ? "Documents BC has without one" : [row.person ?? "Not linked to anyone", row.code].join(" · ");
  return (
    <TR linked={!label}>
      <TD className="w-full max-w-0">
        {label ? (
          <span>{label}</span>
        ) : (
          <Link from="/$market" to="/$market/customers" search={{ salesperson: row.code || "-" } as never} className="block truncate underline-offset-2 hover:underline">
            {name}
          </Link>
        )}
        {sub && <span className="block truncate text-xs text-ink-3">{sub}</span>}
      </TD>
      {targeted && <TDNum from="md">{row.attainment === null ? "" : percent(row.attainment)}</TDNum>}
      <TDNum bar={share}>{money(row.now.revenue)}</TDNum>
      <TDNum from="sm">{change(row.now.revenue, row.revenueBefore) ?? ""}</TDNum>
      <TDNum from="md">{row.now.revenue ? percent(row.now.margin) : ""}</TDNum>
      <TDNum from="xl">{row.now.invoices ? formatCount(row.now.invoices) : ""}</TDNum>
      <TDNum from="xl">{row.now.linesPerInvoice ?? ""}</TDNum>
      <TDNum from="md">{row.now.customers ? formatCount(row.now.customers) : ""}</TDNum>
      <TDNum from="xl">{row.newCustomers ? formatCount(row.newCustomers) : ""}</TDNum>
      <TDNum from="xl">{row.silent ? formatCount(row.silent) : ""}</TDNum>
      <TDNum from="md">{row.overdue ? money(row.overdue) : ""}</TDNum>
    </TR>
  );
}
